import { CollectingType, ReturningType } from 'src/services/api/types'
import { AppRoutingPath } from '../../configs/routes.config'
import ArrayIterator from './ArrayIterator'
import { action, computed, makeObservable, observable } from 'mobx'

export default class FormStepsStore<I, T> {
  public formData!: {
    information: I
    transaction: T
  }
  public editTransactionId: string | undefined

  public transactionType?: ReturningType | CollectingType

  updateTransactionRows = action((updater: (rows: any[]) => any[]) => {
    const tx = this.getTransactionUnsafe()
    tx.rows = updater(tx.rows)
  })

  removeTransactionRow = action((id: string | number) => {
    const tx = this.getTransactionUnsafe()
    tx.rows = tx.rows.filter((r: any) => String(r?.id) !== String(id))
  })

  updateTransactionField = action(
    (rowId: string | number, field: string, newValue: any) => {
      const tx = this.getTransactionUnsafe()
      const row = tx.rows.find((r: any) => String(r?.id) === String(rowId))

      if (row[field] && typeof row[field] === 'object' && 'value' in row[field]) {
        row[field].value = newValue
      } else {
        row[field] = {
          ...(typeof row[field] === 'object' ? row[field] : {}),
          value: newValue,
        }
      }
    }
  )

  updateInformationField = action((key: string, value: any) => {
    const info = (this.formData as any).information as any
    info[key] = value
  })

  resetTransactionData = action(() => {
    if (!this._defaultFormData) return

    this.formData.transaction = JSON.parse(
      JSON.stringify(this._defaultFormData.transaction)
    )
  })

  protected _defaultFormData!: {
    information: I
    transaction: T
  }
  private readonly _iterator: ArrayIterator<AppRoutingPath>

  constructor(public routingPaths: AppRoutingPath[]) {
    makeObservable(this, {
      setFormData: action,
      formData: observable,
      resetFormData: action,
      editTransactionId: observable,
      setEditTransactionId: action,
      skipToNextPath: computed,
      skipToPreviousPath: computed,
      isEditMode: computed,
      iterator: computed,
      initFormData: action,
      transactionType: observable,
      setTransactionType: action,
    })
    this._iterator = new ArrayIterator<AppRoutingPath>(routingPaths)
  }

  get iterator() {
    return this._iterator
  }

  setEditTransactionId(id: string | undefined) {
    this.editTransactionId = id
  }

  setFormData(value: { information: I; transaction: T }) {
    this.formData = value
  }

  setTransactionType(type: ReturningType | CollectingType) {
    this.transactionType = type
  }

  resetFormData() {
    this.initFormData(this._defaultFormData)
  }

  initFormData(value: { information: I; transaction: T }) {
    this._defaultFormData = value
    this.setFormData(value)
  }

  private getTransactionUnsafe() {
    return (this.formData as any).transaction as any
  }

  get isEditMode(): boolean {
    return this.editTransactionId !== undefined
  }

  get skipToNextPath(): string {
    return `${this.iterator.next()}${
      this.editTransactionId ? `/${this.editTransactionId}` : ''
    }`
  }

  get skipToPreviousPath() {
    return `${this.iterator.previous()}${
      this.editTransactionId ? `/${this.editTransactionId}` : ''
    }`
  }
}
